import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Box,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  Checkbox,
  FormControlLabel,
  Alert,
  Collapse,
  Card,
  CardContent,
  Chip,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EnhancedSearch from './common/EnhancedSearch';
import DurationSelector from './DurationSelector';
import config from '../config';
import OrderCreationAttachments from './OrderCreationAttachments';
import OrderAttachments from './OrderAttachments';
import PricingService from '../services/pricingService';
import { getCurrentDateForInput } from '../utils/dateUtils';
import { createStructuredNote, parseNotesBlob } from '../utils/noteUtils';
import NoteCard from './notes/NoteCard';

// Helper function to calculate weeks between two dates
const calculateWeeks = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) {
    return 0;
  }
  
  const diffTime = Math.abs(end - start);
  const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
  return diffWeeks;
};

// Helper function to check if school has Top Spot with insufficient duration
const hasTopSpotDurationIssue = (school, group) => {
  if (!school.addons?.topSpot) return false;
  const weeks = calculateWeeks(group.startDate, group.endDate);
  return weeks > 0 && weeks < 12;
};

// Renders formData.salesNotes/designNotes - each of which may be
// a blob containing several notes joined together - as individually
// attributed, consistently-styled note cards.
const NotesHistoryPreview = ({ formData, fields = { general: 'salesNotes', design: 'designNotes' } }) => {
  const sections = [
    { field: fields.general, title: 'Sales Notes', noteType: 'general', color: 'primary', bgColor: 'blue.50', borderColor: 'blue.200' },
    { field: fields.design, title: 'Design Notes', noteType: 'design', color: 'secondary', bgColor: 'purple.50', borderColor: 'purple.200' }
  ];

  return (
    <Stack spacing={2}>
      {sections.map(section => {
        const notes = parseNotesBlob(formData[section.field], { idPrefix: section.field, noteType: section.noteType });
        if (notes.length === 0) return null;
        return (
          <Box key={section.field}>
            <Typography variant="caption" color={section.color} fontWeight="bold" display="block" gutterBottom>
              {section.title}
            </Typography>
            <Stack spacing={1}>
              {notes.map(note => (
                <NoteCard key={note.id} note={note} color={section.color} bgColor={section.bgColor} borderColor={section.borderColor} />
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

const AdvertisingOrderForm = ({ 
  formData = {}, 
  setFormData, 
  organizations, 
  handleAudienceChange, 
  addAudience, 
  removeAudience, 
  lockedOrganization = null, 
  onTopSpotConflictsChange = null,
  isEditingOrder = false,
  editingOrderId = null
}) => {
  const { user, getAccessTokenSilently } = useAuth0();
  
  // State for attachments during order creation
  const [tempAttachments, setTempAttachments] = useState([]);
  const [savedAttachments, setSavedAttachments] = useState([]);

  // State for new note entry (mirrors OfferForm pattern)
  const [noteType, setNoteType] = useState('general');
  const [newNote, setNewNote] = useState('');

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    // Use the same structured "(date) User from Source: content" format used
    // everywhere else (offer creation, notes dialog) so every note - no matter
    // where it was added - parses and displays consistently.
    const userName = user?.name || user?.email || 'User';
    const formatted = createStructuredNote(newNote.trim(), userName, 'order', noteType);
    if (noteType === 'design') {
      setFormData(prev => ({ ...prev, designNotes: prev.designNotes ? prev.designNotes + '\n\n' + formatted : formatted }));
    } else {
      setFormData(prev => ({ ...prev, salesNotes: prev.salesNotes ? prev.salesNotes + '\n\n' + formatted : formatted }));
    }
    setNewNote('');
  };
  
  // Initialize tempAttachments from formData if provided (e.g., from offer)
  useEffect(() => {
    if (formData.tempAttachments && formData.tempAttachments.length > 0) {
      setTempAttachments(formData.tempAttachments);
    }
  }, [formData.tempAttachments]);
  
  // State for tracking top spot conflicts (user has selected top spot and there's an existing one)
  const [topSpotConflicts, setTopSpotConflicts] = useState({});
  // State for tracking which schools already have top spot taken (shown proactively before user selects it)
  const [topSpotTakenBySchool, setTopSpotTakenBySchool] = useState({});

  // State for groups (replacing audiences)
  // Each group has: channel, adType, startDate, endDate, eLinkType, eLinkContent, groupDiscount, groupDiscountType, addons (gif), and schools array
  // Each school has: organization_id, price, discount, discountType, addons (including topSpot), manualPriceOverride
  const [groups, setGroups] = useState([
    {
      id: 1,
    channel: '',
    adType: '',
    startDate: '',
    endDate: '',
    eLinkType: 'None',
      eLinkContent: '',
      groupDiscount: '',
      groupDiscountType: '$',
      addons: {},
      schools: [
        {
          id: 1,
          organization_id: '',
          price: '',
          discount: '',
          discountType: '$',
          addons: {},
          manualPriceOverride: false
        }
      ]
    }
  ]);

  // Sync groups to formData.audiences for backend compatibility
  useEffect(() => {
    const audiences = [];
    groups.forEach((group) => {
      group.schools.forEach((school) => {
        // Apply group discount if school has no individual discount (handles empty string, 0, or NaN)
        const hasSchoolDiscount = parseFloat(school.discount) > 0;
        const effectiveDiscount = (!hasSchoolDiscount && parseFloat(group.groupDiscount) > 0) 
          ? group.groupDiscount 
          : school.discount;
        const effectiveDiscountType = (!hasSchoolDiscount && parseFloat(group.groupDiscount) > 0)
          ? group.groupDiscountType
          : school.discountType;
        
        // Merge group addons (gif) with school addons (topSpot)
        const mergedAddons = {
          ...(group.addons || {}),
          ...(school.addons || {})
        };
        
        audiences.push({
          organization_id: school.organization_id,
          channel: group.channel,
          adType: group.adType,
          startDate: group.startDate,
          endDate: group.endDate,
          eLinkType: group.eLinkType,
          eLinkContent: group.eLinkContent,
          addons: mergedAddons,
          price: school.price,
          discount: effectiveDiscount,
          discountType: effectiveDiscountType,
          adGroup: group.id, // Use group ID as adGroup for backend compatibility
          manualPriceOverride: school.manualPriceOverride || false
        });
      });
    });
    setFormData(prev => ({ ...prev, audiences }));
  }, [groups]);

  // Initialize groups from formData.audiences when editing
  useEffect(() => {
    if (formData.audiences && formData.audiences.length > 0 && groups.length === 1 && !groups[0].channel) {
      // Group audiences by channel, adType, startDate, endDate, eLinkType, eLinkContent
      const groupMap = new Map();
      
      formData.audiences.forEach((audience, index) => {
        const key = `${audience.channel}-${audience.adType}-${audience.startDate}-${audience.endDate}-${audience.eLinkType || 'None'}-${audience.eLinkContent || ''}`;
        
        if (!groupMap.has(key)) {
          // Extract group-level addons (gif)
          const groupAddons = {};
          if (audience.addons?.gif) {
            groupAddons.gif = audience.addons.gif;
          }
          
          groupMap.set(key, {
            id: groupMap.size + 1,
            channel: audience.channel,
            adType: audience.adType,
            startDate: audience.startDate,
            endDate: audience.endDate,
            eLinkType: audience.eLinkType || 'None',
            eLinkContent: audience.eLinkContent || '',
            groupDiscount: '',
            groupDiscountType: '$',
            addons: groupAddons,
            schools: []
          });
        }
        
        // Extract school-level addons (topSpot)
        const schoolAddons = {};
        if (audience.addons?.topSpot) {
          schoolAddons.topSpot = audience.addons.topSpot;
        }
        
        groupMap.get(key).schools.push({
          id: groupMap.get(key).schools.length + 1,
          organization_id: audience.organization_id,
          price: audience.price,
          discount: audience.discount || '0',
          discountType: audience.discountType || '$',
          addons: schoolAddons,
          manualPriceOverride: audience.manualPriceOverride || false
        });
      });
      
      const initialGroups = Array.from(groupMap.values());
      if (initialGroups.length > 0) {
        setGroups(initialGroups);
      }
    }
  }, [formData.audiences]);

  // Populate _selectedOrg for any school rebuilt from saved data (editing orders /
  // creating from offer). Without this the EnhancedSearch value prop is null so the
  // school name doesn't appear in the dropdown even though price and data are correct.
  useEffect(() => {
    const missing = [];
    groups.forEach(group => {
      group.schools.forEach(school => {
        if (school.organization_id && !school._selectedOrg) {
          missing.push(school.organization_id);
        }
      });
    });
    if (missing.length === 0) return;

    let cancelled = false;
    const fetchMissingOrgs = async () => {
      const token = await getAccessTokenSilently();
      const unique = [...new Set(missing)];
      const fetched = {};
      await Promise.all(unique.map(async (orgId) => {
        try {
          const resp = await fetch(`${config.apiUrl}/api/organizations/${orgId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data && data.id) fetched[orgId] = data;
          }
        } catch (e) {
          console.error('Failed to fetch org for school display:', orgId, e);
        }
      }));
      if (cancelled || Object.keys(fetched).length === 0) return;
      setGroups(prev => prev.map(group => ({
        ...group,
        schools: group.schools.map(school => {
          if (school.organization_id && !school._selectedOrg && fetched[school.organization_id]) {
            return { ...school, _selectedOrg: fetched[school.organization_id] };
          }
          return school;
        })
      })));
    };
    fetchMissingOrgs();
    return () => { cancelled = true; };
  // groups is the only dep we need — getAccessTokenSilently and config are stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // Update temp attachments when they change
  React.useEffect(() => {
    // Only update temp attachments, don't overwrite other form data
    if (formData.orderType) {
      // Form is already initialized, just update attachments
      setFormData(prev => ({
        ...prev,
        tempAttachments: tempAttachments
      }));
    } else {
      // First time initialization for new orders
      setFormData(prev => ({
        ...prev,
        orderType: 'ad',
        status: prev.status || 'New',
        tempAttachments: tempAttachments
      }));
    }
  }, [tempAttachments]);

  // Check for existing top spot conflicts when form data changes
  React.useEffect(() => {
    groups.forEach(async (group, groupIndex) => {
      if (group.addons?.topSpot) {
        for (const school of group.schools) {
          if (school.organization_id && group.channel && group.startDate && group.endDate) {
            // Check availability for schools that already have top spot selected
            await checkTopSpotAvailability(group.id, school.organization_id, group.channel, group.startDate, group.endDate);
          }
        }
      }
    });
  }, [groups]);

  // Handle attachment changes
  const handleAttachmentsChange = (attachments) => {
    setTempAttachments(attachments);
    // Also update formData so attachments are available when creating the order
    setFormData(prev => ({ ...prev, tempAttachments: attachments }));
  };

  // Filter organizations based on isBusiness flag for the main dropdown
  const businessOrganizations = (organizations || []).filter(org => 
    org.isBusiness === true || org.isBusiness === 1
  );

  // Filter organizations based on isSchool flag for the audience dropdowns
  // Only show Active schools with Ad Supported payment model
  const schoolOrganizations = (organizations || []).filter(org => 
    (org.isSchool === true || org.isSchool === 1) && 
    (org.schoolStatus === 'Active' || !org.schoolStatus) &&
    (org.paymentModel === 'Ad Supported' || org.paymentModel !== 'Paid')
  );

  // Function to get available channels for an organization
  const getAvailableChannels = (organizationId) => {
    if (!organizationId) return [];
    
    const selectedOrg = organizations.find(org => org.id === organizationId);
    if (!selectedOrg) return [];
    
    const channels = [];
    
    // Add channels based on organization properties
    if (selectedOrg.hasSchoolLink === true || selectedOrg.hasSchoolLink === 1) {
      channels.push("SchoolLink");
    }
    
    if (selectedOrg.hasEPublisher === true || selectedOrg.hasEPublisher === 1) {
      channels.push("ePublisher");
    }
    
    if (selectedOrg.hasPaper === true || selectedOrg.hasPaper === 1) {
      channels.push("Paper");
    }
    
    // If this is a school and no channels are available, provide default channels
    if (channels.length === 0 && (selectedOrg.isSchool === true || selectedOrg.isSchool === 1)) {
      // Default channels for schools - can be customized based on business requirements
      channels.push("SchoolLink", "ePublisher", "Paper");
    }
    
    return channels;
  };

  // Function to get available ad types based on selected channel
  const getAvailableAdTypes = (channel) => {
    if (!channel) return [];
    
    switch (channel) {
      case 'Paper':
        return ['Standard', 'Budget', 'Premium'];
      case 'ePublisher':
        return ['Standard', 'Budget', 'Premium', 'POW'];
      case 'SchoolLink':
        return ['Single Image', 'Carousel', 'Video or Animation', 'Standard', 'Premium'];
      default:
        return [];
    }
  };

  // Function to get organization price, optionally channel-aware
  const getOrganizationPrice = (organizationId, channel = null) => {
    const org = organizations.find(org => org.id === organizationId);
    if (!org) return null;

    // For ePublisher channel: prefer ePublisherCost as the annual base price,
    // then fall back to the reach-based formula if the stored cost is 0.
    if (channel === 'ePublisher' && (org.isSchool === true || org.isSchool === 1)) {
      if (org.ePublisherCost && parseFloat(org.ePublisherCost) > 0) {
        return parseFloat(org.ePublisherCost);
      }
      // Reach-based fallback: (deploymentsPerTerm × 4) × effectiveSubscribers
      const deployments = parseInt(org.deploymentsPerTerm || org.deployments || org.issuesPerTerm, 10) || 0;
      const explicitSubs = parseInt(org.ePublisherSubscribers, 10);
      const effectiveSubs = (!explicitSubs && org.numberOfFamilies)
        ? parseInt(org.numberOfFamilies, 10) * 2
        : explicitSubs || parseInt(org.subscribers || org.numberOfFamilies, 10) || 0;
      if (deployments > 0 && effectiveSubs > 0) {
        const reach = (deployments * 4) * effectiveSubs;
        return reach > 5000 ? 900 + (reach - 5000) * 0.025 : 900;
      }
      return 900; // minimum school ePublisher price
    }

    if (org.price) {
      return parseFloat(org.price);
    }
    return null;
  };

  // Function to calculate price using server-side API with weekly rates
  const calculatePriceWithDates = async (basePrice, channel, adType, startDate, endDate, addons = {}, schoolId = null) => {
    if (!channel || !adType || !startDate || !endDate) {
      return basePrice || 0;
    }
    
    // For POW, we don't need basePrice
    if (adType === 'POW' || (basePrice && !isNaN(parseFloat(basePrice)))) {
      // Continue with calculation
    } else {
      return basePrice || 0;
    }
    
    try {
      // Get the auth token
      const authToken = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'read:current_user'
      });
      
      if (!authToken) {
        console.warn('No auth token available, using base price');
        return basePrice;
      }
      
      // Make direct API call with proper token
      const response = await fetch(`${config.apiUrl}/api/pricing/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          basePrice,
          channel,
          adType,
          startDate,
          endDate,
          addons,
          schoolId
        })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        return result.price;
      } else {
        console.warn('Price calculation failed, using base price:', result.message);
        return basePrice;
      }
    } catch (error) {
      console.error('Price calculation error:', error);
      return basePrice; // Fallback to base price
    }
  };

  // Handle organization selection
  const handleOrganizationChange = (e) => {
    const selectedOrg = organizations.find(org => org.id === e.target.value);
    if (selectedOrg) {
      // Extract values with priority for camelCase properties
      const businessName = selectedOrg.name || selectedOrg.businessName || selectedOrg.business_name || '';
      const businessType = selectedOrg.businessType || selectedOrg.business_type || '';
      
      setFormData({
        ...formData,
        organization_id: selectedOrg.id,
        organization: selectedOrg.id,  // Add organization field for backend compatibility
        // Note: In the database, "organization" field actually stores what we display as "Business Number"
        // This is confusing but we need to maintain compatibility with the existing database structure
        businessNumber: selectedOrg.id.toString(),  // Store organization ID as business number
        business_number: selectedOrg.id.toString(), // Store organization ID as business number
        businessName: businessName,
        business_name: businessName,
        businessType: businessType,
        business_type: businessType
      });
    }
  };

  // Add a new group
  const addGroup = () => {
    const newGroupId = Math.max(...groups.map(g => g.id), 0) + 1;
    setGroups([
      ...groups,
      {
        id: newGroupId,
        channel: '',
        adType: '',
        startDate: '',
        endDate: '',
        eLinkType: 'None',
        eLinkContent: '',
        groupDiscount: '',
        groupDiscountType: '$',
        addons: {},
        schools: [
          {
            id: 1,
            organization_id: '',
            price: '',
            discount: '',
            discountType: '$',
            addons: {},
            manualPriceOverride: false
          }
        ]
      }
    ]);
  };

  // Remove a group
  const removeGroup = (groupId) => {
    setGroups(groups.filter(g => g.id !== groupId));
  };

  // Add a school to a group
  const addSchoolToGroup = (groupId) => {
    setGroups(groups.map(group => {
      if (group.id === groupId) {
        const newSchoolId = Math.max(...group.schools.map(s => s.id), 0) + 1;
        return {
          ...group,
          schools: [
            ...group.schools,
            {
              id: newSchoolId,
              organization_id: '',
              price: '',
              discount: '0',
              discountType: '$',
              addons: {},
              manualPriceOverride: false
            }
          ]
        };
      }
      return group;
    }));
  };

  // Remove a school from a group
  const removeSchoolFromGroup = (groupId, schoolId) => {
    setGroups(groups.map(group => {
      if (group.id === groupId) {
                  return {
          ...group,
          schools: group.schools.filter(s => s.id !== schoolId)
        };
      }
      return group;
    }));
  };

  // Handle group field changes
  const handleGroupChange = (groupId, field, value) => {
    setGroups(groups.map(group => {
      if (group.id === groupId) {
        const updatedGroup = { ...group, [field]: value };
        
        // If channel changes, reset adType and clear prices
        if (field === 'channel') {
          updatedGroup.adType = '';
          // Clear prices for all schools in this group
          updatedGroup.schools = updatedGroup.schools.map(school => ({
            ...school,
            price: ''
          }));
        }
        
        // If adType changes to POW, set end date to 5 weeks after start date
        if (field === 'adType' && value === 'POW' && updatedGroup.startDate) {
          const startDate = new Date(updatedGroup.startDate);
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + (5 * 7)); // 5 weeks = 35 days
          updatedGroup.endDate = endDate.toISOString().split('T')[0];
        }
        
        // If adType changes from POW to something else, unlock the end date (keep current value)
        // No action needed here as the field will automatically become editable
        
        // If adType or channel changes, clear addons that are no longer supported
        if (field === 'adType' || field === 'channel') {
          // Check if GIF addon should be cleared
          // GIF is only supported for: ePublisher Premium, or POW (ePublisher/Paper)
          const gifSupported = (updatedGroup.channel === 'ePublisher' && updatedGroup.adType === 'Premium') ||
                               (updatedGroup.adType === 'POW' && ['ePublisher', 'Paper'].includes(updatedGroup.channel));
          
          if (!gifSupported && updatedGroup.addons?.gif) {
            updatedGroup.addons = { ...updatedGroup.addons, gif: false };
          }
          
          // Check if Top Spot addon should be cleared for all schools
          // Top Spot is only supported for: ePublisher Standard/Premium (not POW)
          const topSpotSupported = updatedGroup.channel === 'ePublisher' && 
                                   ['Standard', 'Premium'].includes(updatedGroup.adType) &&
                                   updatedGroup.adType !== 'POW';
          
          if (!topSpotSupported) {
            updatedGroup.schools = updatedGroup.schools.map(school => {
              if (school.addons?.topSpot) {
                // Clear top spot conflict for this school
                const conflictKey = `${groupId}-${school.id}`;
                setTopSpotConflicts(prev => {
                  const newConflicts = { ...prev };
                  delete newConflicts[conflictKey];
                  if (onTopSpotConflictsChange) {
                    onTopSpotConflictsChange(newConflicts);
                  }
                  return newConflicts;
                });
                
                return {
                  ...school,
                  addons: { ...school.addons, topSpot: false }
                };
              }
              return school;
            });
            // Clear proactive top spot status when channel no longer supports it
            setTopSpotTakenBySchool(prev => {
              const updated = { ...prev };
              updatedGroup.schools.forEach(school => {
                delete updated[`${groupId}-${school.id}`];
              });
              return updated;
            });
          } else if (field === 'channel' && updatedGroup.channel === 'ePublisher') {
            // Channel changed to ePublisher � proactively check top spot for existing schools
            updatedGroup.schools.forEach(school => {
              if (school.organization_id) {
                checkSchoolTopSpotStatus(groupId, school.id, school.organization_id, 'ePublisher');
              }
            });
          }
        }
        
        // If eLinkType changes to None, clear eLinkContent
      if (field === 'eLinkType' && value === 'None') {
          updatedGroup.eLinkContent = '';
        }
        
        // Recalculate prices for all schools in this group when dates, channel, or adType change
        if ((field === 'startDate' || field === 'endDate' || field === 'channel' || field === 'adType') && updatedGroup.channel && updatedGroup.adType && updatedGroup.startDate && updatedGroup.endDate) {
          updatedGroup.schools.forEach((school, schoolIndex) => {
            if (school.organization_id && !school.manualPriceOverride) {
              calculateSchoolPrice(groupId, school.id, school.organization_id, updatedGroup.channel, updatedGroup.adType, updatedGroup.startDate, updatedGroup.endDate, school.addons || {});
            }
          });
        }
        
        return updatedGroup;
      }
      return group;
    }));
  };

  // Handle school field changes
  // orgObject is optionally passed when field === 'organization_id' so we can
  // store the full org object alongside the id. This prevents the EnhancedSearch
  // value prop from needing to look the org up in the limited local `organizations`
  // array (which may only hold a fraction of the orgs when the DB is large).
  const handleSchoolChange = async (groupId, schoolId, field, value, orgObject = null) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    const school = group.schools.find(s => s.id === schoolId);
    if (!school) return;
    
    // Update the state first
    setGroups(groups.map(g => {
      if (g.id === groupId) {
                return {
          ...g,
          schools: g.schools.map(school => {
            if (school.id === schoolId) {
              const updates = { [field]: value };
              // When the org is being set, also cache the full object so the
              // EnhancedSearch value prop resolves without scanning the local array.
              if (field === 'organization_id') {
                updates._selectedOrg = orgObject || null;
              }
              return { ...school, ...updates };
            }
            return school;
          })
        };
      }
      return g;
    }));
    
    // Then handle price calculations based on the field that changed
    // If organization changes, calculate price (only if manual override is not enabled)
    if (field === 'organization_id' && value && group.channel && group.adType && group.startDate && group.endDate && !school.manualPriceOverride) {
      // Use setTimeout to ensure state has updated
      setTimeout(() => {
        calculateSchoolPrice(groupId, schoolId, value, group.channel, group.adType, group.startDate, group.endDate, school.addons || {});
      }, 0);
    }

    // Proactively check top spot status when a school is selected on an ePublisher group
    if (field === 'organization_id' && value && group.channel === 'ePublisher') {
      checkSchoolTopSpotStatus(groupId, schoolId, value, group.channel);
    }
    
    // Clear top spot status when school is deselected
    if (field === 'organization_id' && !value) {
      const key = `${groupId}-${schoolId}`;
      setTopSpotTakenBySchool(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
    
    // If manual price override is being disabled, recalculate price
    if (field === 'manualPriceOverride' && value === false && school.organization_id && group.channel && group.adType && group.startDate && group.endDate) {
      // Force calculation even though the state hasn't updated yet
      setTimeout(() => {
        calculateSchoolPrice(groupId, schoolId, school.organization_id, group.channel, group.adType, group.startDate, group.endDate, school.addons || {}, true);
      }, 0);
    }
  };

  // Calculate price for a specific school
  const calculateSchoolPrice = async (groupId, schoolId, organizationId, channel, adType, startDate, endDate, addons = {}, forceCalculate = false) => {
    try {
      const selectedSchool = organizations.find(org => org.id === organizationId);
      
      if (!selectedSchool || !channel || !adType || !startDate || !endDate) {
        return;
      }

      // Check if manual price override is enabled for this school (unless forced)
      if (!forceCalculate) {
        const group = groups.find(g => g.id === groupId);
        const school = group?.schools.find(s => s.id === schoolId);
        if (school?.manualPriceOverride) {
          // Don't calculate price if manual override is enabled
          return;
        }
      }

      // Get the actual base price from the organization (channel-aware for ePublisher)
      let basePrice = getOrganizationPrice(organizationId, channel);
      
      if (!basePrice) {
        const fallbackPrice = (selectedSchool.isSchool === true || selectedSchool.isSchool === 1) ? 900 : 100;
        basePrice = fallbackPrice;
      }

      // Use async pricing calculation with dates
      const calculatedPrice = await calculatePriceWithDates(
        basePrice, 
        channel, 
        adType, 
        startDate, 
        endDate,
        addons,
        organizationId
      );
      
      setGroups(currentGroups => currentGroups.map(g => {
        if (g.id === groupId) {
          return {
            ...g,
            schools: g.schools.map(school => {
              if (school.id === schoolId) {
                return {
                  ...school,
                  price: calculatedPrice.toFixed(2)
                };
              }
              return school;
            })
          };
        }
        return g;
      }));
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  // Check top spot availability for a school
  const checkTopSpotAvailability = async (groupId, schoolId, organizationId, channel, startDate = null, endDate = null) => {
    try {
      const result = await PricingService.checkTopSpotAvailability(organizationId, channel, startDate, endDate);
      
      const conflictKey = `${groupId}-${schoolId}`;
      
      // Update the conflicts state
      setTopSpotConflicts(prev => {
        const newConflicts = {
          ...prev,
          [conflictKey]: result.data.hasConflict || false
        };
        // Notify parent component of conflicts change
        if (onTopSpotConflictsChange) {
          onTopSpotConflictsChange(newConflicts);
        }
        return newConflicts;
      });
      
      return !result.data.hasConflict;
    } catch (error) {
      console.error('Error checking top spot availability:', error);
      // Clear any existing conflict for this school on error
      const conflictKey = `${groupId}-${schoolId}`;
      setTopSpotConflicts(prev => {
        const newConflicts = { ...prev };
        delete newConflicts[conflictKey];
        // Notify parent component of conflicts change
        if (onTopSpotConflictsChange) {
          onTopSpotConflictsChange(newConflicts);
        }
        return newConflicts;
      });
      return true; // Assume available on error to not block the user
    }
  };

  // Proactively check if a school already has top spot taken (regardless of whether user has selected it)
  const checkSchoolTopSpotStatus = async (groupId, schoolId, organizationId, channel) => {
    if (!organizationId || channel !== 'ePublisher') return;
    try {
      const result = await PricingService.checkTopSpotAvailability(organizationId, channel);
      const key = `${groupId}-${schoolId}`;
      setTopSpotTakenBySchool(prev => ({
        ...prev,
        [key]: result.data.hasConflict || false
      }));
    } catch (error) {
      console.error('Error checking top spot status for school:', error);
    }
  };

  // Handle addon changes for a specific school
  const handleSchoolAddonChange = async (groupId, schoolId, addonType, checked) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;
    
    const school = group.schools.find(s => s.id === schoolId);
    if (!school) return;
    
    // If selecting top spot, check availability
    if (addonType === 'topSpot' && checked) {
      if (school.organization_id && group.channel && group.startDate && group.endDate) {
        await checkTopSpotAvailability(groupId, schoolId, school.organization_id, group.channel, group.startDate, group.endDate);
      }
    }
    
    setGroups(groups.map(g => {
      if (g.id === groupId) {
        return {
          ...g,
          schools: g.schools.map(s => {
            if (s.id === schoolId) {
              const updatedAddons = { ...s.addons, [addonType]: checked };
              
              // Recalculate price when addons change
              if (s.organization_id && g.channel && g.adType && g.startDate && g.endDate) {
                calculateSchoolPrice(groupId, schoolId, s.organization_id, g.channel, g.adType, g.startDate, g.endDate, updatedAddons);
              }
              
              return { ...s, addons: updatedAddons };
            }
            return s;
          })
        };
      }
      return g;
    }));
    
    // Clear top spot conflict when unchecking top spot
    if (addonType === 'topSpot' && !checked) {
      const conflictKey = `${groupId}-${schoolId}`;
      setTopSpotConflicts(prev => {
        const newConflicts = { ...prev };
        delete newConflicts[conflictKey];
        // Notify parent component of conflicts change
        if (onTopSpotConflictsChange) {
          onTopSpotConflictsChange(newConflicts);
        }
        return newConflicts;
      });
    }
  };

  // Handle addon changes for a group (gif)
  const handleGroupAddonChange = async (groupId, addonType, checked) => {
    setGroups(groups.map(g => {
      if (g.id === groupId) {
        const updatedAddons = { ...g.addons, [addonType]: checked };
        
        // Recalculate prices for all schools when group addons change
        if (g.channel && g.adType && g.startDate && g.endDate) {
          g.schools.forEach(school => {
            if (school.organization_id) {
              // Merge group addons with school addons for price calculation
              const mergedAddons = { ...updatedAddons, ...(school.addons || {}) };
              calculateSchoolPrice(groupId, school.id, school.organization_id, g.channel, g.adType, g.startDate, g.endDate, mergedAddons);
            }
          });
        }
        
        return { ...g, addons: updatedAddons };
      }
      return g;
    }));
  };

  // Calculate subtotal for a school with $ or % discount support
  // If school discount is 0, apply group discount
  const calculateSchoolSubTotal = (school, group) => {
    const price = parseFloat(school.price || 0);
    const schoolDiscount = parseFloat(school.discount || 0);
    const groupDiscount = parseFloat(group.groupDiscount || 0);
    
    // Use school discount if set, otherwise use group discount
    const effectiveDiscount = schoolDiscount > 0 ? schoolDiscount : groupDiscount;
    const effectiveDiscountType = schoolDiscount > 0 ? (school.discountType || '$') : (group.groupDiscountType || '$');
    
    if (effectiveDiscount === 0) {
      return price;
    }
    
    let subtotal;
    if (effectiveDiscountType === '%') {
      // Percentage discount: subtract percentage of price
      const discountAmount = (price * effectiveDiscount) / 100;
      subtotal = Math.max(0, price - discountAmount);
    } else {
      // Dollar discount: subtract fixed amount
      subtotal = Math.max(0, price - effectiveDiscount);
    }
    
    return subtotal;
  };

  // Calculate subtotal for a group
  const calculateGroupSubTotal = (group) => {
    return group.schools.reduce((total, school) => {
      return total + calculateSchoolSubTotal(school, group);
    }, 0);
  };

  // Calculate total for all groups
  const calculateTotal = () => {
    return groups.reduce((total, group) => {
      return total + calculateGroupSubTotal(group);
    }, 0);
  };

  return (
    <Grid container spacing={3}>
      {/* Business Information */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Business Information
        </Typography>
        <Paper sx={{ p: 2 }}>
          {lockedOrganization ? (
            // Read-only business information when locked (from offer)
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={lockedOrganization.name || formData.business_name || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  sx={{ backgroundColor: 'grey.50' }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Business ID"
                  value={lockedOrganization.id || formData.business_number || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  sx={{ backgroundColor: 'grey.50' }}
                />
              </Grid>
            </Grid>
          ) : (
            // Editable business information when not locked
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box mb={2}>
                  <EnhancedSearch
                    label="Business / Organisation"
                    placeholder="Search by name, type, or location..."
                    endpoint="/organizations/search?type=business"
                    value={
                      formData.organization_id && formData.businessName ? 
                      { id: formData.organization_id, name: formData.businessName } : 
                      null
                    }
                    onSelect={(selected) => {
                      // Update form data with selected organization
                      if (selected) {
                        const updates = {
                          organization_id: selected.id,
                          organization: selected.id,  // Add organization field for backend compatibility
                          businessNumber: selected.id.toString(), // Set to the organization ID
                          business_number: selected.id.toString(), // Set to the organization ID
                          businessName: selected.name || '',
                          business_name: selected.name || '',
                          businessType: selected.businessType || '',
                          business_type: selected.businessType || ''
                        };
                        
                        // Remove the automatic audience update - let user choose
                        // This was causing the confusing behavior
                        
                        setFormData({
                          ...formData,
                          ...updates
                        });
                      }
                    }}
                    limitResults={15}
                    autoFocus
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ID"
                  name="business_number"
                  value={formData.business_number || ''}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                />
              </Grid>
            </Grid>
          )}
        </Paper>
      </Grid>

      {/* Product Type */}
      <Grid item xs={12} sx={{ mt: lockedOrganization ? 3 : 0 }}>
        <FormControl fullWidth required>
          <InputLabel>Product Type</InputLabel>
          <Select
            value={formData.productType || ''}
            onChange={(e) => setFormData({ ...formData, productType: e.target.value })}
            label="Product Type"
          >
            <MenuItem value="Repeat">Repeat</MenuItem>
            <MenuItem value="New">New</MenuItem>
            <MenuItem value="Lead">Lead</MenuItem>
            <MenuItem value="Update">Update</MenuItem>
            <MenuItem value="New to School">New to School</MenuItem>
            <MenuItem value="Reactivated">Reactivated</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Status Field - Hidden, defaults to 'New' */}
      {/* Status is now handled automatically in the backend and defaults to 'New' */}

      {/* Groups Section */}
      {groups.map((group, groupIndex) => (
        <Grid item xs={12} key={group.id}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Group {groupIndex + 1}
            </Typography>
                {groups.length > 1 && (
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => removeGroup(group.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

            <Grid container spacing={2}>
                {/* First Line: Channel, Ad Type, Start Date, End Date */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth required>
                  <InputLabel>Channel</InputLabel>
                  <Select
                      value={group.channel || ''}
                      onChange={(e) => handleGroupChange(group.id, 'channel', e.target.value)}
                    label="Channel"
                  >
                    <MenuItem value="SchoolLink">SchoolLink</MenuItem>
                    <MenuItem value="ePublisher">ePublisher</MenuItem>
                    <MenuItem value="Paper">Paper</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth required>
                  <InputLabel>Ad Type</InputLabel>
                  <Select
                      value={group.adType || ''}
                      onChange={(e) => handleGroupChange(group.id, 'adType', e.target.value)}
                    label="Ad Type"
                      disabled={!group.channel}
                    >
                      {group.channel ? (
                        getAvailableAdTypes(group.channel).map(adType => (
                      <MenuItem key={adType} value={adType}>{adType}</MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>Select a channel first</MenuItem>
                      )}
                  </Select>
                </FormControl>
              </Grid>
              
                <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                    value={group.startDate || ''}
                    onChange={(e) => {
                      handleGroupChange(group.id, 'startDate', e.target.value);
                      // If POW is selected, automatically update end date to 5 weeks after new start date
                      if (group.adType === 'POW' && e.target.value) {
                        const startDate = new Date(e.target.value);
                        const endDate = new Date(startDate);
                        endDate.setDate(endDate.getDate() + (5 * 7)); // 5 weeks = 35 days
                        setTimeout(() => {
                          handleGroupChange(group.id, 'endDate', endDate.toISOString().split('T')[0]);
                        }, 0);
                      }
                    }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: getCurrentDateForInput() }}
                    required
                />
              </Grid>
              
                <Grid item xs={12} sm={6} md={3}>
                  {group.adType === 'POW' ? (
                <TextField
                  fullWidth
                      label="End Date (POW Fixed)"
                      type="date"
                      value={group.endDate || ''}
                      disabled
                      helperText="POW ads have a fixed 5-week duration"
                  InputLabelProps={{ shrink: true }}
                />
                  ) : (
                    <DurationSelector
                      startDate={group.startDate}
                      endDate={group.endDate}
                      onEndDateChange={(value) => handleGroupChange(group.id, 'endDate', value)}
                      label="End Date"
                      required
                      defaultDuration={{ value: 1, unit: 'years' }}
                      minDate={getCurrentDateForInput()}
                    />
                  )}
              </Grid>
              
                {/* Second Line: eLink Type, eLink Content, Group Discount, GIF Addon, Group Total */}
                <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>eLink Type</InputLabel>
                  <Select
                      value={group.eLinkType || 'None'}
                      onChange={(e) => handleGroupChange(group.id, 'eLinkType', e.target.value)}
                    label="eLink Type"
                  >
                    <MenuItem value="Website">Website</MenuItem>
                    <MenuItem value="Email">Email</MenuItem>
                    <MenuItem value="None">None</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
                {group.eLinkType !== 'None' ? (
                  <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="eLink Content"
                      value={group.eLinkContent || ''}
                      onChange={(e) => handleGroupChange(group.id, 'eLinkContent', e.target.value)}
                      required={group.eLinkType !== 'None'}
                      helperText={(group.eLinkType === 'Website') ? "Enter full URL" : 
                                  (group.eLinkType === 'Email') ? "Enter email" : ""}
                  />
                </Grid>
                ) : (
                  <Grid item xs={12} sm={6} md={3} />
                )}

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Group Discount"
                    type="number"
                    value={group.groupDiscount || ''}
                    onChange={(e) => handleGroupChange(group.id, 'groupDiscount', e.target.value)}
                    placeholder="0"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Select
                            value={group.groupDiscountType || '$'}
                            onChange={(e) => handleGroupChange(group.id, 'groupDiscountType', e.target.value)}
                            variant="standard"
                            disableUnderline
                          >
                            <MenuItem value="$">$</MenuItem>
                            <MenuItem value="%">%</MenuItem>
                          </Select>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* GIF Addon Option */}
                {((group.channel === 'ePublisher' && group.adType === 'Premium') ||
                  (group.adType === 'POW' && ['ePublisher', 'Paper'].includes(group.channel))) && (
                  <Grid item xs={12} sm={6} md={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                          checked={group.addons?.gif || false}
                          onChange={(e) => handleGroupAddonChange(group.id, 'gif', e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                            Gif {group.adType === 'POW' ? '(+$100)' : '(+$10/week)'}
                              </Typography>
                            </Box>
                          }
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6} md={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pt: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Group Total: 
                              </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, ml: 1, color: 'primary.main' }}>
                      ${calculateGroupSubTotal(group).toFixed(2)}
                              </Typography>
                    </Box>
                  </Grid>

                {/* Schools in this group */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 1 }}>
                      Schools
                    </Typography>
                    <Chip label={group.schools.length} size="small" color="primary" />
                  </Box>
                  
                  {group.schools.map((school, schoolIndex) => (
                    <Paper key={school.id} sx={{ p: 1.5, mb: 0.5, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        {group.schools.length > 1 && (
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => removeSchoolFromGroup(group.id, school.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      <Grid container spacing={2}>
                        {/* Single Line: School, Top Spot, Price, Discount, Subtotal */}
                        <Grid item xs={12} md={4}>
                          <EnhancedSearch
                            label="Select School"
                            placeholder="Search for school..."
                            endpoint="/organizations/search?type=school"
                            value={school._selectedOrg || null}
                            onSelect={(selected) => {
                              // Pass the full org object so handleSchoolChange can cache
                              // it — avoids depending on the limited local array for value.
                              handleSchoolChange(group.id, school.id, 'organization_id', selected ? selected.id : null, selected || null);
                            }}
                            limitResults={15}
                            filterOptions={(options) => {
                              // Get all selected school IDs in this group (excluding current school)
                              const selectedSchoolIds = group.schools
                                .filter(s => s.id !== school.id && s.organization_id)
                                .map(s => parseInt(s.organization_id));
                              
                              return options.filter(opt => {
                                // Exclude already selected schools in this group
                                if (selectedSchoolIds.includes(opt.id)) return false;
                                
                                // Basic filters
                                const isActiveSchool = (opt.isSchool === true || opt.isSchool === 1) &&
                                  (opt.schoolStatus === 'Active' || !opt.schoolStatus) &&
                                  (opt.paymentModel === 'Ad Supported' || opt.paymentModel !== 'Paid');
                                
                                if (!isActiveSchool) return false;
                                
                                // Filter by channel if group has a channel selected
                                if (group.channel) {
                                  if (group.channel === 'SchoolLink') {
                                    return opt.hasSchoolLink === true || opt.hasSchoolLink === 1;
                                  } else if (group.channel === 'ePublisher') {
                                    return opt.hasEPublisher === true || opt.hasEPublisher === 1;
                                  } else if (group.channel === 'Paper') {
                                    return opt.hasPaper === true || opt.hasPaper === 1;
                                  }
                                }
                                
                                return true;
                              });
                            }}
                    required
                  />
                </Grid>

                        {/* Top Spot Option - Compact */}
                        {group.channel === 'ePublisher' && 
                         ['Standard', 'Premium'].includes(group.adType) &&
                         group.adType !== 'POW' && school.organization_id && (
                          <Grid item xs={12} md={1}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ mb: 0.5 }}>
                                Top Spot
                              </Typography>
                              {topSpotTakenBySchool[`${group.id}-${school.id}`] && !school.addons?.topSpot ? (
                                <Tooltip title="Top Spot is already taken at this school for ePublisher">
                                  <Chip
                                    label="Taken"
                                    size="small"
                                    color="error"
                                    sx={{ fontSize: '0.7rem', height: '24px' }}
                                  />
                                </Tooltip>
                              ) : (
                                <Tooltip title={topSpotTakenBySchool[`${group.id}-${school.id}`] ? "Warning: Top Spot already taken at this school" : "Top Spot (+$5/week) - Minimum 12 weeks"}>
                                  <Checkbox
                                    checked={school.addons?.topSpot || false}
                                    onChange={(e) => handleSchoolAddonChange(group.id, school.id, 'topSpot', e.target.checked)}
                                    color={hasTopSpotDurationIssue(school, group) || topSpotConflicts[`${group.id}-${school.id}`] ? 'warning' : 'primary'}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </Grid>
                        )}

                        <Grid item xs={12} md={2}>
                          <TextField
                            fullWidth
                            label="Price"
                            value={school.manualPriceOverride ? (school.price || '') : (school.price ? `$${parseFloat(school.price).toFixed(2)}` : '$0.00')}
                            onChange={(e) => {
                              if (school.manualPriceOverride) {
                                // Remove $ and any non-numeric characters except decimal point
                                const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                                handleSchoolChange(group.id, school.id, 'price', numericValue);
                              }
                            }}
                            InputProps={{
                              readOnly: !school.manualPriceOverride,
                              startAdornment: school.manualPriceOverride ? <InputAdornment position="start">$</InputAdornment> : null,
                            }}
                            size="small"
                            type={school.manualPriceOverride ? "number" : "text"}
                          />
                </Grid>

                        <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Discount"
                      type="number"
                            value={(school.discount && school.discount !== '0') ? school.discount : (group.groupDiscount || '')}
                            onChange={(e) => handleSchoolChange(group.id, school.id, 'discount', e.target.value)}
                      InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                      <Select
                                    value={(school.discount && school.discount !== '0') ? (school.discountType || '$') : (group.groupDiscountType || '$')}
                                    onChange={(e) => handleSchoolChange(group.id, school.id, 'discountType', e.target.value)}
                        variant="standard"
                        disableUnderline
                      >
                        <MenuItem value="$">$</MenuItem>
                        <MenuItem value="%">%</MenuItem>
                      </Select>
                                </InputAdornment>
                              ),
                            }}
                            size="small"
                            placeholder={(group.groupDiscount && group.groupDiscount !== '0') ? group.groupDiscount : '0'}
                          />
                </Grid>

                        <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                            label="Subtotal"
                            value={`$${calculateSchoolSubTotal(school, group).toFixed(2)}`}
                            InputProps={{ readOnly: true }}
                            size="small"
                    />
                  </Grid>

                        <Grid item xs={12} md={1}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography variant="caption" sx={{ mb: 0.5, textAlign: 'center' }}>
                              Manual Price
                            </Typography>
                            <Checkbox
                              checked={school.manualPriceOverride || false}
                              onChange={(e) => handleSchoolChange(group.id, school.id, 'manualPriceOverride', e.target.checked)}
                              size="small"
                            />
                          </Box>
                        </Grid>

                        {/* Advertising Objections */}
                        {school._selectedOrg?.advertisingObjectives && (
                          <Grid item xs={12}>
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                <strong>Advertising Objections:</strong> {school._selectedOrg.advertisingObjectives}
                              </Typography>
                            </Alert>
                          </Grid>
                        )}

                        {/* Warnings below the row */}
                        {school.organization_id && (
                          <Grid item xs={12}>
                            <Collapse in={hasTopSpotDurationIssue(school, group)}>
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                  <strong>Top Spot:</strong> Requires minimum 12 weeks. Current: {calculateWeeks(group.startDate, group.endDate)} weeks.
                                </Typography>
                              </Alert>
                            </Collapse>
                            
                            <Collapse in={topSpotTakenBySchool[`${group.id}-${school.id}`] === true && !school.addons?.topSpot}>
                              <Alert severity="info" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                  <strong>Top Spot Taken:</strong> This school already has an active Top Spot order for {group.channel}.
                                </Typography>
                              </Alert>
                            </Collapse>

                            <Collapse in={topSpotConflicts[`${group.id}-${school.id}`] === true}>
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                  <strong>Top Spot Conflict:</strong> This school already has Top Spot for {group.channel} during these dates.
                                </Typography>
                              </Alert>
                            </Collapse>
                          </Grid>
                        )}
              </Grid>
            </Paper>
                  ))}

                  {/* Add Another School Button */}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => addSchoolToGroup(group.id)}
                    sx={{ mt: 1 }}
                  >
                    Add Another School
                  </Button>
          </Grid>

              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={addGroup}
          startIcon={<AddIcon />}
        >
          Add Another Group
        </Button>
      </Grid>

      {/* Total field for all groups */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Total"
          value={calculateTotal().toFixed(2)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            readOnly: true,
          }}
          sx={{ mb: 2 }}
        />
      </Grid>

      {/* Notes Section */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Order Notes
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {/* Display categorized notes from offer if any - parsed into individual,
              consistently-styled note cards rather than shown as one raw blob */}
          {(formData.salesNotesFromOffer || formData.designNotesFromOffer) && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Notes from Offer:
              </Typography>
              <NotesHistoryPreview
                formData={formData}
                fields={{ general: 'salesNotesFromOffer', design: 'designNotesFromOffer' }}
              />
            </Box>
          )}
          
          {/* Invoice Later Section */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" gutterBottom>
            Invoice Settings:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.invoiceLater || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData({ 
                      ...formData, 
                      invoiceLater: checked,
                      // Clear invoice date if unchecking
                      invoiceDate: checked ? formData.invoiceDate : ''
                    });
                  }}
                  color="primary"
                />
              }
              label="Invoice Later"
            />
            {!!formData.invoiceLater && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  type="date"
                  label="Invoice Date"
                  value={formData.invoiceDate ? formData.invoiceDate.split('T')[0] : ''}
                  onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  helperText="Select the date when this order should be invoiced"
                />
              </Box>
            )}
          </Box>

          {/* Notes Section */}
          <Divider sx={{ my: 3 }} />
          {!isEditingOrder && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon fontSize="small" />
                Add New Note
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" gutterBottom display="block">Note Type:</Typography>
                <Stack direction="row" spacing={1}>
                  <Chip label="General" size="small"
                    variant={noteType === 'general' ? 'filled' : 'outlined'}
                    color={noteType === 'general' ? 'primary' : 'default'}
                    onClick={() => setNoteType('general')} />
                  <Chip label="Design" size="small"
                    variant={noteType === 'design' ? 'filled' : 'outlined'}
                    color={noteType === 'design' ? 'secondary' : 'default'}
                    onClick={() => setNoteType('design')} />
                </Stack>
              </Box>
              <TextField
                fullWidth multiline rows={3}
                placeholder="Enter your note here..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                variant="outlined" sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" size="small" startIcon={<AddIcon />}
                  onClick={handleAddNote} disabled={!newNote.trim()}>
                  Add Note
                </Button>
              </Box>

              {/* Show accumulated notes - each parsed into its own consistent card */}
              {(formData.salesNotes || formData.designNotes) && (
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>Notes History</Typography>
                  <NotesHistoryPreview formData={formData} />
                </Box>
              )}
            </Box>
          )}
          {isEditingOrder && (formData.salesNotes || formData.designNotes) && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Order Notes:</Typography>
              <NotesHistoryPreview formData={formData} />
            </Box>
          )}
        </Paper>
      </Grid>

      {/* Order Attachments */}
      <Grid item xs={12}>
        {isEditingOrder && editingOrderId ? (
          <OrderAttachments
            orderId={editingOrderId}
            attachments={savedAttachments}
            onAttachmentsChange={setSavedAttachments}
            tempAttachments={tempAttachments}
            onTempAttachmentsChange={handleAttachmentsChange}
            type="order"
          />
        ) : (
          <OrderCreationAttachments
            attachments={tempAttachments}
            onAttachmentsChange={handleAttachmentsChange}
            title="Order Attachments"
            maxFiles={10}
          />
        )}
      </Grid>
    </Grid>
  );
};

// Export helper function for external validation
export const checkOrderTopSpotDurationRequirements = (audiences = []) => {
  // Check each audience for Top Spot duration issues
  return !audiences.some(audience => {
    // Create a school object and group object for validation
    const school = {
      addons: audience.addons || {}
    };
    const group = {
      startDate: audience.startDate,
      endDate: audience.endDate
    };
    return hasTopSpotDurationIssue(school, group);
  });
};

// Helper function to check if there are any top spot conflicts
export const checkOrderTopSpotAvailabilityRequirements = (topSpotConflicts = {}) => {
  return !Object.values(topSpotConflicts).some(hasConflict => hasConflict === true);
};

// Combined validation for both duration and availability
export const checkOrderTopSpotRequirements = (audiences = [], topSpotConflicts = {}) => {
  return checkOrderTopSpotDurationRequirements(audiences) && 
         checkOrderTopSpotAvailabilityRequirements(topSpotConflicts);
};

export default AdvertisingOrderForm; 
